/**
 * What to call the list of things a business offers.
 *
 * ── Why this file exists ───────────────────────────────────────────
 *
 * One table holds it — `provider_items` — and it requires nothing but a name.
 * Price, image and video are all optional, and nothing anywhere checks the
 * category. So a pandit has always been able to list "Gruh Shanti", and a CPA
 * "FBAR filing", exactly as a sweet shop lists "Kaju katli (500g)".
 *
 * Not one of them ever did. The reason was never the schema — it was the
 * WORDS. Every label around the feature is a shop word: catalogue, shopfront,
 * shelf, stall, products, price. A pandit opening that screen sees a grocery
 * interface and correctly concludes it is not for him.
 *
 * So the fix is not a second table for services. It is calling the same thing
 * by the name its owner would use.
 *
 * ── Why the API does not send these strings ────────────────────────
 *
 * It sends `category.kind`, which is the FACT — does this trade sell goods,
 * services, or both. The wording is a copy decision, and copy belongs to the
 * surface showing it: web says one thing, Android may say another, and a
 * translation changes both without a deploy of the API.
 *
 * What must NOT drift is the RULE — that the words follow `kind` — which is
 * why it lives in one file per client rather than being typed at each screen.
 */

export type ProviderKind = 'goods' | 'service' | 'both' | string;

export interface OfferingWords {
  /** The tab and the heading. */
  section: string;
  /** What one row IS, used in empty states and confirmations. */
  singular: string;
  /** The name field's placeholder — a real example from that trade. */
  namePlaceholder: string;
  /** What the free-text price field is called to somebody in this trade. */
  priceLabel: string;
  pricePlaceholder: string;
  /** One line under the heading explaining what this list is for. */
  blurb: string;
}

const GOODS: OfferingWords = {
  section: 'Catalogue',
  singular: 'item',
  namePlaceholder: 'Kaju katli (500g)',
  priceLabel: 'Price',
  pricePlaceholder: '$22 · from $40 · by the kilo',
  blurb:
    "What the shop sells. It hangs off the business rather than off a market stall, so a shop trading in March still has somewhere to put its products — a haat stall then selects from this.",
};

const SERVICE: OfferingWords = {
  section: 'Services',
  singular: 'service',
  namePlaceholder: 'Gruh Shanti · Griha Pravesh · Satyanarayan Katha',
  priceLabel: 'What you charge',
  pricePlaceholder: 'from $151 · $75/hour · ask',
  blurb:
    "Everything you offer, named the way somebody would ask for it. Naming each one separately is worth more than a paragraph: it is what somebody searching for that exact ceremony finds, and it answers the question people ask most and listings answer least — what does it cost.",
};

const BOTH: OfferingWords = {
  ...SERVICE,
  section: 'What you offer',
  singular: 'entry',
  namePlaceholder: 'Family thali (serves 6) · Fall and pico stitching',
  blurb:
    "Everything you sell or do, named the way somebody would ask for it. Goods and services sit in the same list — a caterer sells trays and books events, and both belong here.",
};

/** The words for a trade. Unknown kinds fall back to the neutral pair. */
export function offeringWords(kind: ProviderKind | null | undefined): OfferingWords {
  if (kind === 'goods') return GOODS;
  if (kind === 'service') return SERVICE;
  return BOTH;
}
